import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const FREQ = [
  { v: "daily", l: "Giornaliero", perYear: 365.25 },
  { v: "weekly", l: "Settimanale", perYear: 52.1786 },
  { v: "monthly", l: "Mensile", perYear: 12 },
  { v: "quarterly", l: "Trimestrale", perYear: 4 },
  { v: "yearly", l: "Annuale", perYear: 1 },
  { v: "one_off", l: "Una tantum", perYear: 0 },
];
const CATEGORIES = ["affitto", "personale", "energia", "acqua", "internet/telefono",
  "commercialista", "assicurazioni", "software", "manutenzione", "noleggi", "tributi", "altro"];

// same normalization as backend
function toMonthly(amount, freq) {
  const a = Number(amount || 0);
  switch (freq) {
    case "daily": return a * 30.4375;
    case "weekly": return a * 4.348;
    case "monthly": return a;
    case "quarterly": return a / 3;
    case "yearly": return a / 12;
    case "one_off": return 0;
    default: return 0;
  }
}
function toYearly(amount, freq) {
  const a = Number(amount || 0);
  const f = FREQ.find((x) => x.v === freq);
  return f ? a * f.perYear : 0;
}

const Tip = ({ text }) => (
  <span title={text} className="ml-1 text-xs text-neutral-400 cursor-help">ⓘ</span>
);

function CostForm({ open, onOpenChange, existing, onSaved, labourEngineActive }) {
  const isEdit = !!existing;
  const [f, setF] = useState(() => existing || {
    name: "", category: "affitto", amount: 0, frequency: "monthly",
    start_date: new Date().toISOString().slice(0, 10), end_date: "", active: true, notes: "",
  });
  const [applyFrom, setApplyFrom] = useState(new Date().toISOString().slice(0, 10));
  const [useEffectiveDate, setUseEffectiveDate] = useState(false);

  useEffect(() => {
    if (existing) setF({ ...existing, end_date: existing.end_date || "" });
  }, [existing]);

  const monthlyEq = toMonthly(f.amount, f.frequency);
  const yearlyEq = toYearly(f.amount, f.frequency);
  const err = (() => {
    if (!f.name?.trim()) return "Nome obbligatorio";
    if (Number(f.amount) < 0) return "Importo non può essere negativo";
    if (f.frequency === "one_off" && !f.start_date) return "Una tantum richiede data";
    if (f.end_date && f.start_date && f.end_date < f.start_date) return "Fine prima di inizio";
    return null;
  })();

  const save = async () => {
    if (err) { toast.error(err); return; }
    try {
      const cur = (await api.get("/settings")).data;
      const fin = { ...(cur.financial || {}) };
      const list = [...(fin.fixed_costs || [])];
      const payload = { ...f, amount: Number(f.amount) };
      if (!payload.end_date) delete payload.end_date;

      if (isEdit) {
        if (useEffectiveDate) {
          // Historical preservation: close the old row, open a new one from applyFrom
          const oldIdx = list.findIndex((x) => x.id === existing.id);
          if (oldIdx >= 0) {
            const prevDay = new Date(applyFrom);
            prevDay.setDate(prevDay.getDate() - 1);
            list[oldIdx] = { ...list[oldIdx], end_date: prevDay.toISOString().slice(0, 10) };
          }
          list.push({ ...payload, id: crypto.randomUUID(), start_date: applyFrom, end_date: undefined });
        } else {
          const idx = list.findIndex((x) => x.id === existing.id);
          if (idx >= 0) list[idx] = { ...list[idx], ...payload };
        }
      } else {
        list.push({ ...payload, id: crypto.randomUUID() });
      }
      fin.fixed_costs = list;
      await api.put("/settings/financial", fin);
      toast.success(isEdit ? "Costo aggiornato" : "Costo aggiunto");
      onOpenChange(false); onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const isPersonale = (f.category || "").toLowerCase() === "personale";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="cost-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Modifica costo fisso" : "Nuovo costo fisso"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <div className="label-eyebrow mb-1">Nome</div>
            <Input data-testid="cost-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Es. Affitto locale" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Categoria</div>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="cost-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Frequenza</div>
            <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}>
              <SelectTrigger data-testid="cost-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>{FREQ.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Importo €</div>
            <Input data-testid="cost-amount" type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Data inizio {f.frequency === "one_off" && "*"}</div>
            <Input data-testid="cost-start" type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} />
          </div>
          {f.frequency !== "one_off" && (
            <div>
              <div className="label-eyebrow mb-1">Data fine (opzionale)</div>
              <Input data-testid="cost-end" type="date" value={f.end_date || ""} onChange={(e) => setF({ ...f, end_date: e.target.value })} />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Switch checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="cost-active" />
            <span className="text-sm">Attivo</span>
          </div>
          <div className="col-span-2">
            <div className="label-eyebrow mb-1">Note</div>
            <Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>

        {/* Preview */}
        <div className="mt-3 p-3 rounded bg-[#FDFBF7] border border-[#E2D9C8] text-sm" data-testid="cost-preview">
          {f.frequency === "one_off" ? (
            <div>Impatto una tantum: <b>{fmtEUR(f.amount)}</b> il {f.start_date || "—"}</div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>Equivalente mensile: <b data-testid="preview-monthly">{fmtEUR(monthlyEq)}</b></div>
              <div>Equivalente annuale: <b data-testid="preview-yearly">{fmtEUR(yearlyEq)}</b></div>
            </div>
          )}
        </div>

        {isPersonale && labourEngineActive && (
          <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-300 text-xs text-amber-900" data-testid="warn-double-count">
            ⚠️ Questo costo personale sarà <b>escluso dal break-even</b> perché il costo del personale viene già calcolato dai turni.
          </div>
        )}

        {isEdit && f.frequency !== "one_off" && (
          <div className="mt-2 p-2 rounded bg-blue-50 border border-blue-200 text-xs">
            <label className="flex items-center gap-2">
              <input type="checkbox" data-testid="use-effective-date"
                checked={useEffectiveDate} onChange={(e) => setUseEffectiveDate(e.target.checked)} />
              Applica modifica da una data (non altera lo storico)
            </label>
            {useEffectiveDate && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs">Applica da:</span>
                <Input type="date" value={applyFrom} onChange={(e) => setApplyFrom(e.target.value)}
                       className="w-40 h-8" data-testid="effective-from" />
              </div>
            )}
          </div>
        )}

        {err && <div className="text-xs text-red-700 mt-2">{err}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={!!err} data-testid="cost-save" className="bg-neutral-900 text-[#FDFBF7]">
            {isEdit ? "Salva modifiche" : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ConfBadge = ({ level }) => {
  const map = { actual: ["bg-emerald-100 text-emerald-800", "Reale / Completo"],
                estimated: ["bg-amber-100 text-amber-800", "Stimato"],
                insufficient_data: ["bg-neutral-200 text-neutral-600", "Dati insufficienti"] };
  const [cls, label] = map[level] || map.insufficient_data;
  return <span className={`inline-block px-2 py-0.5 rounded text-xs ${cls}`}>{label}</span>;
};

export default function EconomicConfig() {
  const [costs, setCosts] = useState([]);
  const [setup, setSetup] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [filter, setFilter] = useState("");

  const load = async () => {
    const [set, sc, cov, emp, sh] = await Promise.all([
      api.get("/settings"),
      api.get("/profitability/setup-completeness"),
      api.get("/profitability/coverage?days=30"),
      api.get("/employees"),
      api.get("/shifts"),
    ]);
    setCosts((set.data.financial || {}).fixed_costs || []);
    setSetup(sc.data); setCoverage(cov.data);
    setEmployees(emp.data || []); setShifts(sh.data || []);
  };
  useEffect(() => { load(); }, []);

  const labourEngineActive = useMemo(() =>
    shifts.length > 0 && employees.some((e) => Number(e.hourly_cost) > 0), [shifts, employees]);

  const missingRateEmp = employees.filter((e) => !(Number(e.hourly_cost) > 0));

  const filtered = costs.filter((c) => !filter || (c.name || "").toLowerCase().includes(filter.toLowerCase()) || (c.category || "").includes(filter.toLowerCase()));
  const totalMonthlyRecurring = costs.filter((c) => c.active && c.frequency !== "one_off").reduce((s, c) => s + toMonthly(c.amount, c.frequency), 0);
  const totalYearlyRecurring = costs.filter((c) => c.active && c.frequency !== "one_off").reduce((s, c) => s + toYearly(c.amount, c.frequency), 0);
  const oneOffs = costs.filter((c) => c.frequency === "one_off");

  const remove = async (c) => {
    if (!window.confirm(`Eliminare "${c.name}"?\nSuggerimento: se il costo è stato utilizzato in analisi passate, considera "Disattiva" per preservare lo storico.`)) return;
    const cur = (await api.get("/settings")).data;
    const fin = { ...(cur.financial || {}) };
    fin.fixed_costs = (fin.fixed_costs || []).filter((x) => x.id !== c.id);
    await api.put("/settings/financial", fin);
    toast.success("Costo eliminato"); load();
  };
  const toggleActive = async (c) => {
    const cur = (await api.get("/settings")).data;
    const fin = { ...(cur.financial || {}) };
    fin.fixed_costs = (fin.fixed_costs || []).map((x) => x.id === c.id ? { ...x, active: !x.active } : x);
    await api.put("/settings/financial", fin);
    toast.success(c.active ? "Disattivato" : "Attivato"); load();
  };
  const duplicate = async (c) => {
    const cur = (await api.get("/settings")).data;
    const fin = { ...(cur.financial || {}) };
    fin.fixed_costs = [...(fin.fixed_costs || []), { ...c, id: crypto.randomUUID(), name: `${c.name} (copia)` }];
    await api.put("/settings/financial", fin);
    toast.success("Duplicato"); load();
  };

  return (
    <div data-testid="econ-config-page">
      <PageHeader eyebrow="Amministrazione" title="Configurazione Economica"
        subtitle="Costi fissi, personale, copertura dati e stato del setup — tutto in un posto." />

      {/* Stato configurazione */}
      {setup && (
        <div className="mb-8 border border-[#E2D9C8] rounded-lg p-6 bg-white" data-testid="setup-block">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Stato configurazione</div>
              <div className="font-serif text-3xl mt-1">{setup.score_pct}% completa</div>
              <div className="text-xs text-neutral-500 mt-1">{setup.ok_count}/{setup.total} controlli superati</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {setup.checks.map((c) => (
              <div key={c.key} className={`p-3 rounded border ${c.ok ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid={`check-${c.key}`}>
                <div className="text-sm font-medium">{c.ok ? "✓" : "!"} {c.label}</div>
                <div className="text-xs text-neutral-600">{c.detail}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Costi fissi */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="font-serif text-2xl">Costi fissi <Tip text="Un costo che non dipende direttamente dal numero di prodotti venduti." /></h2>
            <div className="text-xs text-neutral-500">Ricorrente {fmtEUR(totalMonthlyRecurring)}/mese · {fmtEUR(totalYearlyRecurring)}/anno · {oneOffs.length} una tantum</div>
          </div>
          <div className="flex gap-2">
            <Input placeholder="Cerca…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" data-testid="cost-search" />
            <Button data-testid="cost-add" onClick={() => setDialog({ open: true, editing: null })} className="bg-neutral-900 text-[#FDFBF7]">+ Nuovo</Button>
          </div>
        </div>

        <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-hidden">
          <table className="w-full text-sm zebra">
            <thead><tr className="text-left label-eyebrow">
              <th className="p-3">Nome</th><th className="p-3">Categoria</th><th className="p-3">Frequenza</th>
              <th className="p-3 text-right">Importo</th>
              <th className="p-3 text-right">= mensile <Tip text="Conversione media del costo per confrontarlo con gli altri." /></th>
              <th className="p-3 text-right">= annuale</th>
              <th className="p-3">Validità</th><th className="p-3">Stato</th><th className="p-3"></th>
            </tr></thead>
            <tbody data-testid="costs-tbody">
              {filtered.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-neutral-400">Nessun costo configurato.</td></tr>}
              {filtered.map((c) => {
                const excluded = labourEngineActive && (c.category || "").toLowerCase() === "personale";
                return (
                  <tr key={c.id} className={`border-t border-[#E2D9C8] ${!c.active ? "opacity-50" : ""}`} data-testid={`cost-row-${c.id}`}>
                    <td className="p-3">
                      {c.name}
                      {excluded && <Badge className="ml-2 bg-amber-100 text-amber-900 border-amber-300" data-testid={`badge-excluded-${c.id}`}>Escluso dal break-even</Badge>}
                    </td>
                    <td className="p-3 text-xs text-neutral-500">{c.category}</td>
                    <td className="p-3 text-xs">{FREQ.find((f) => f.v === c.frequency)?.l || c.frequency}</td>
                    <td className="p-3 text-right">{fmtEUR(c.amount)}</td>
                    <td className="p-3 text-right">{c.frequency === "one_off" ? "—" : fmtEUR(toMonthly(c.amount, c.frequency))}</td>
                    <td className="p-3 text-right">{c.frequency === "one_off" ? "—" : fmtEUR(toYearly(c.amount, c.frequency))}</td>
                    <td className="p-3 text-xs text-neutral-500">{c.start_date || "—"} → {c.end_date || "∞"}</td>
                    <td className="p-3"><Badge className={c.active ? "bg-emerald-100 text-emerald-900" : "bg-neutral-200"}>{c.active ? "Attivo" : "Non attivo"}</Badge></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, editing: c })} data-testid={`cost-edit-${c.id}`}>Modifica</Button>
                      <Button size="sm" variant="outline" className="ml-1" onClick={() => toggleActive(c)} data-testid={`cost-toggle-${c.id}`}>{c.active ? "Disattiva" : "Attiva"}</Button>
                      <Button size="sm" variant="outline" className="ml-1" onClick={() => duplicate(c)}>Duplica</Button>
                      <Button size="sm" variant="outline" className="ml-1 text-red-700" onClick={() => remove(c)} data-testid={`cost-delete-${c.id}`}>Elimina</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Personale */}
      <div className="mb-8 border border-[#E2D9C8] rounded-lg p-6 bg-white" data-testid="personnel-block">
        <h2 className="font-serif text-2xl mb-3">Personale — Stato configurazione</h2>
        <div className="text-sm mb-2">
          Dipendenti con costo orario configurato: <b>{employees.length - missingRateEmp.length}/{employees.length}</b>
        </div>
        {missingRateEmp.length > 0 && (
          <div className="p-3 rounded bg-amber-50 border border-amber-300 text-sm">
            <div className="font-medium mb-1">Manca costo orario per:</div>
            <ul className="list-disc list-inside">
              {missingRateEmp.slice(0, 10).map((e) => (
                <li key={e.id}>{e.first_name} {e.last_name} <Link to="/employees" className="text-xs underline ml-2">apri scheda</Link></li>
              ))}
            </ul>
          </div>
        )}
        {labourEngineActive && (
          <div className="mt-3 text-xs text-neutral-600">
            ✓ Labour engine attivo — il costo personale viene calcolato dai turni. Eventuali voci "personale" tra i costi fissi vengono escluse dal break-even.
          </div>
        )}
      </div>

      {/* Copertura dati */}
      {coverage && (
        <div className="mb-8 border border-[#E2D9C8] rounded-lg p-6 bg-white" data-testid="coverage-block">
          <h2 className="font-serif text-2xl mb-3">Qualità dati economici <Tip text="Percentuale delle vendite per cui conosciamo il costo storico delle materie prime." /></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard testid="cov-count" label="Vendite con snapshot" value={`${coverage.coverage_count_pct}%`} />
            <StatCard testid="cov-rev" label="Fatturato con snapshot" value={`${coverage.coverage_revenue_pct}%`} />
            <StatCard testid="cov-sales" label="Vendite considerate" value={String(coverage.sales_total)} />
            <div className="border border-[#E2D9C8] rounded-lg p-4 bg-[#FDFBF7]">
              <div className="label-eyebrow mb-1">Affidabilità margine</div>
              <ConfBadge level={coverage.confidence} />
            </div>
          </div>
          <div className="text-xs text-neutral-500 mt-3">Analisi ultimi 30 giorni · solo dati reali (esclude demo)</div>
        </div>
      )}

      <CostForm open={dialog.open} onOpenChange={(v) => setDialog({ ...dialog, open: v })}
                existing={dialog.editing} onSaved={load} labourEngineActive={labourEngineActive} />
    </div>
  );
}
